/**
 * Cloudflare Turnstile widget — bot protection on the signup email step
 * (see auth.service.ts#startSignup on the backend for the server-side
 * verify call this token feeds). Loads Cloudflare's script lazily (only
 * when this component mounts), same pattern as GoogleSignInButton.tsx.
 *
 * Renders NOTHING if VITE_TURNSTILE_SITE_KEY isn't set, matching every
 * other optional-integration component in this codebase — startSignup
 * skips verification server-side when unconfigured, so an absent widget
 * here is consistent, not a broken/degraded state.
 */
import { useEffect, useRef, useState } from 'react';

const TURNSTILE_SITE_KEY = import.meta.env['VITE_TURNSTILE_SITE_KEY'] as string | undefined;
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

export const isTurnstileConfigured = Boolean(TURNSTILE_SITE_KEY);

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: (() => void) | undefined;
          'error-callback'?: (() => void) | undefined;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'flexible' | 'compact';
        }
      ) => string;
      reset: (widgetId?: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;
let hasAttemptedLoad = false;

/** Same retry-after-failure reasoning as GoogleSignInButton's loadGsiScript —
 *  a transient network blip on first mount shouldn't permanently break the
 *  widget for the rest of the browser session. */
function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  const isRetry = hasAttemptedLoad;
  hasAttemptedLoad = true;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = isRetry ? null : document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Turnstile')));
      return;
    }
    if (isRetry) document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`)?.remove();
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Turnstile'));
    document.head.appendChild(script);
  }).catch((err) => {
    scriptPromise = null;
    throw err;
  });
  return scriptPromise;
}

interface TurnstileWidgetProps {
  /** Called with the verification token every time the widget solves (or
   *  re-solves after expiry) — the parent just holds the latest value and
   *  sends it along with the next form submit. */
  onToken: (token: string) => void;
  /** Called when a previously-issued token expires — the parent should
   *  clear its held token so a stale one is never submitted. */
  onExpire?: () => void;
}

export default function TurnstileWidget({ onToken, onExpire }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !containerRef.current) return;

    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: onToken,
          'expired-callback': onExpire,
          'error-callback': () => setFailed(true),
          size: 'flexible',
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fails open on the frontend — a Turnstile outage shouldn't block signup
  // entirely; the backend already fails open the same way when Cloudflare's
  // siteverify is unreachable (see turnstile.client.ts), so hiding a broken
  // widget here is consistent with that, not a workaround for it.
  if (!TURNSTILE_SITE_KEY || failed) return null;

  return <div ref={containerRef} style={{ width: '100%' }} />;
}
